// GET/POST /providers y PATCH /providers/:id — la nómina de contratistas
// de la organización.
//
// Las tablas (`providers`, `provider_contacts`, `provider_categories`)
// existen desde hace tiempo y estaban completas; lo que no existía era
// una ruta que las leyera. Se escribían sólo de dos maneras: inline desde
// el drawer de delegación de facility, y por la hoja «Proveedores» del
// workbook. Ninguna superficie de postventa las tocaba, así que el panel
// no tenía de dónde elegir a quién asignarle una observación. El PATCH
// llegó con Administración · Contratistas (autogestión 2026-10-30 §2.6):
// editar la ficha y pausar o reactivar al contratista.
import { z } from "zod";
import { emailSchema } from "./auth.js";
/** `providers.status`. La columna es texto libre; el enum vive acá. */
export const providerStatusSchema = z.enum(["activo", "pausado"]);
export const providerListItemSchema = z.object({
    id: z.string().uuid(),
    shortName: z.string(),
    legalName: z.string().nullable(),
    /** Texto libre hoy (la hoja del workbook junta varias categorías con
     *  «;»). El anclaje bueno a futuro es `provider_categories`, que ya
     *  reserva `isPrimary` para «el proveedor por defecto de esta
     *  categoría» — cuando eso se use, la sugerencia del drawer deja de
     *  depender de comparar strings. */
    specialty: z.string().nullable(),
    contactEmail: z.string().nullable(),
    contactPhone: z.string().nullable(),
    /** La lista trae activos y pausados por igual (nunca filtró por estado
     *  y la pantalla de administración necesita ver ambos). Quien muestre
     *  la nómina para asignar decide si esconde a los pausados. */
    status: providerStatusSchema,
    /** Observaciones asignadas a este contratista que todavía no están
     *  resueltas ni cerradas. Es la columna «Trabajos activos» de la
     *  nómina, y es calculable desde que la asignación escribe
     *  `findings.provider_id`. */
    activeJobs: z.number().int().nonnegative(),
});
export const providerListResponse = z.object({
    providers: z.array(providerListItemSchema),
});
// Validaciones por campo, compartidas por el alta y la edición: lo que
// vale para crear vale para corregir.
const providerShortName = z.string().trim().min(1).max(200);
const providerLegalName = z.string().trim().min(1).max(200);
const providerSpecialty = z.string().trim().min(1).max(200);
const providerContactPhone = z.string().trim().min(1).max(40);
export const providerCreateBody = z
    .object({
    shortName: providerShortName,
    legalName: providerLegalName.optional(),
    specialty: providerSpecialty.optional(),
    contactEmail: emailSchema.optional(),
    contactPhone: providerContactPhone.optional(),
})
    .strict()
    // Un contratista sin ninguna forma de contactarlo no sirve para lo
    // único que la nómina existe para hacer: mandarle el enlace.
    .refine((b) => Boolean(b.contactEmail) || Boolean(b.contactPhone), {
    message: "se necesita al menos un correo o un teléfono",
    path: ["contactEmail"],
});
export const providerCreateResponse = z.object({
    provider: providerListItemSchema,
    /** false cuando el nombre ya existía en la organización y la fila se
     *  reusó en vez de duplicarse. El panel ramifica el copy con esto en
     *  vez de afirmar «contratista creado» sobre una fila que ya estaba. */
    created: z.boolean(),
});
export const providerParams = z.object({ id: z.string().uuid() });
// PATCH /providers/:id — la ficha y el estado. Todo opcional pero al
// menos un campo: un PATCH vacío es un error del cliente, no un no-op.
// Ningún campo se puede dejar en null desde acá (el drawer no ofrece
// «borrar»), así que la regla «al menos un correo o un teléfono» del
// alta se conserva sola.
export const providerPatchBody = z
    .object({
    shortName: providerShortName.optional(),
    legalName: providerLegalName.optional(),
    specialty: providerSpecialty.optional(),
    contactEmail: emailSchema.optional(),
    contactPhone: providerContactPhone.optional(),
    status: providerStatusSchema.optional(),
})
    .strict()
    .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: "se necesita al menos un campo",
});
export const providerPatchResponse = z.object({
    provider: providerListItemSchema,
    /** Observaciones asignadas a este contratista que siguen abiertas
     *  (misma definición que `activeJobs`). Viaja aparte porque el toast
     *  de «Desactivar» lo dice en palabras: pausar no desasigna nada. */
    openAssigned: z.number().int().nonnegative(),
});
//# sourceMappingURL=providers.js.map